import React, { useEffect, useState, useCallback } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription 
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Search, 
  X, 
  Tag, 
  FileText, 
  Database,
  ChevronRight,
  ChevronDown
} from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { 
  closeGlossaryModal,
  setSearchQuery,
  setSelectedTags,
  toggleTag,
  clearSearch,
  fetchGlossaryTerms,
  fetchGlossaryTags,
  setSelectedTerm
} from '@/store/slices/glossarySlice';
import { GlossaryTermWithRelations } from '@shared/types/glossary';
import { cn } from '@/lib/utils';

interface ExpandedTerms {
  [termId: string]: boolean;
}

const GlossaryModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const { 
    isModalOpen,
    terms,
    loading,
    error,
    searchQuery,
    selectedTags,
    availableTags
  } = useAppSelector(state => state.glossary);
  
  // Add safety checks for undefined values
  const safeTerms = terms || [];
  // Use tags directly with null checks where needed

  const [expandedTerms, setExpandedTerms] = useState<ExpandedTerms>({});
  const [searchDebounceTimer, setSearchDebounceTimer] = useState<NodeJS.Timeout | null>(null);

  // Load data when modal opens
  useEffect(() => {
    if (isModalOpen) {
      dispatch(fetchGlossaryTags());
      dispatch(fetchGlossaryTerms({ 
        search: searchQuery, 
        tags: selectedTags || [],
        limit: 50 
      }));
    }
  }, [dispatch, isModalOpen]);

  // Debounced search
  useEffect(() => {
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer);
    }

    const timer = setTimeout(() => {
      dispatch(fetchGlossaryTerms({ 
        search: searchQuery, 
        tags: selectedTags || [],
        limit: 50 
      }));
    }, 300);

    setSearchDebounceTimer(timer);

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [searchQuery, selectedTags, dispatch]);

  const handleClose = () => {
    dispatch(closeGlossaryModal());
    dispatch(clearSearch());
    setExpandedTerms({});
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setSearchQuery(e.target.value));
  };

  const handleTagToggle = (tag: string) => {
    dispatch(toggleTag(tag));
  };

  const handleClearAll = () => {
    dispatch(clearSearch());
    setExpandedTerms({});
  };

  const toggleTermExpansion = useCallback((termId: string) => {
    setExpandedTerms(prev => ({
      ...prev,
      [termId]: !prev[termId]
    }));
  }, []);

  const renderTermCard = useCallback((term: GlossaryTermWithRelations) => {
    const isExpanded = expandedTerms[term.id];
    
    return (
      <Card key={term.id} className="mb-3">
        <CardHeader 
          className="pb-3 cursor-pointer hover:bg-accent/50 transition-colors"
          onClick={() => toggleTermExpansion(term.id)}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <CardTitle className="text-lg font-semibold text-foreground flex items-center gap-2">
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                )}
                {term.term}
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                {term.description}
              </p>
              
              {/* Tags */}
              {term.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {term.tags.map((tag: string) => (
                    <Badge 
                      key={tag} 
                      variant="secondary" 
                      className="text-xs"
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
        </CardHeader>

        {isExpanded && (
          <CardContent className="pt-0">
            {/* Full description */}
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="h-4 w-4 text-primary" />
                <span className="font-medium text-sm text-foreground">Description</span>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed pl-6">
                {term.description}
              </p>
            </div>

            {/* Technical description */}
            {term.technicalDescription && (
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <Database className="h-4 w-4 text-emerald-600" />
                  <span className="font-medium text-sm text-foreground">Technical Details</span>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed pl-6 font-mono bg-muted/50 p-2 rounded">
                  {term.technicalDescription}
                </p>
              </div>
            )}

            {/* Aliases */}
            {term.aliases.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <Tag className="h-4 w-4 text-orange-600" />
                  <span className="font-medium text-sm text-foreground">Also known as</span>
                </div>
                <div className="flex flex-wrap gap-1 pl-6">
                  {term.aliases.map((alias: string) => (
                    <Badge key={alias} variant="outline" className="text-xs">
                      {alias}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Related terms */}
            {term.relatedTerms.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <ChevronRight className="h-4 w-4 text-purple-600" />
                  <span className="font-medium text-sm text-foreground">Related Terms</span>
                </div>
                <div className="flex flex-wrap gap-2 pl-6">
                  {term.relatedTerms.map((relatedTerm) => (
                    <Button
                      key={relatedTerm.id}
                      variant="ghost"
                      size="sm"
                      className="text-xs h-6 px-2 text-primary hover:text-primary/80 hover:bg-primary/10"
                      onClick={(e) => {
                        e.stopPropagation();
                        dispatch(setSearchQuery(relatedTerm.term));
                      }}
                    >
                      {relatedTerm.term}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        )}
      </Card>
    );
  }, [expandedTerms, toggleTermExpansion, dispatch]);

  return (
    <Dialog open={isModalOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl h-[90vh] p-0 bg-background border-border flex flex-col">
        <DialogHeader className="px-6 py-4 border-b border-border bg-background flex-shrink-0">
          <DialogTitle className="text-xl font-semibold">
            Project Glossary
          </DialogTitle>
          <DialogDescription className="sr-only">
            Search and browse project glossary terms with their definitions, technical descriptions, and related terminology.
          </DialogDescription>
        </DialogHeader>

        {/* Search and filters */}
        <div className="px-6 py-4 border-b border-border bg-muted/30 flex-shrink-0">
          <div className="flex gap-3 mb-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search terms, descriptions, or aliases..."
                value={searchQuery}
                onChange={handleSearchChange}
                className="pl-10 bg-background border-input text-foreground placeholder:text-muted-foreground"
              />
            </div>
            {(searchQuery || (selectedTags && selectedTags.length > 0)) && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleClearAll}
                className="whitespace-nowrap"
              >
                Clear All
              </Button>
            )}
          </div>

          {/* Tag filters */}
          {availableTags && availableTags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <span className="text-sm text-muted-foreground mr-2 self-center">Filter by tags:</span>
              {availableTags.slice(0, 10).map(tag => (
                <Badge
                  key={tag}
                  variant={(selectedTags && selectedTags.includes(tag)) ? "default" : "outline"}
                  className={cn(
                    "cursor-pointer transition-colors text-xs",
                    (selectedTags && selectedTags.includes(tag)) 
                      ? "bg-primary text-primary-foreground hover:bg-primary/80" 
                      : "hover:bg-accent hover:text-accent-foreground"
                  )}
                  onClick={() => handleTagToggle(tag)}
                >
                  {tag}
                </Badge>
              ))}
              {availableTags.length > 10 && (
                <Badge variant="outline" className="text-xs">
                  +{availableTags.length - 10} more
                </Badge>
              )}
            </div>
          )}
        </div>

        {/* Content - Scrollable Area */}
        <div className="flex-1 overflow-hidden">
          <ScrollArea className="h-full w-full">
            <div className="px-6 py-4">
              {loading && safeTerms.length === 0 ? (
                <div className="space-y-3">
                  {[...Array(3)].map((_, i) => (
                    <Card key={i}>
                      <CardHeader>
                        <Skeleton className="h-6 w-48" />
                        <Skeleton className="h-4 w-full" />
                        <div className="flex gap-2 mt-2">
                          <Skeleton className="h-5 w-16" />
                          <Skeleton className="h-5 w-20" />
                        </div>
                      </CardHeader>
                    </Card>
                  ))}
                </div>
              ) : error ? (
                <div className="text-center py-12">
                  <div className="text-destructive mb-2">Error loading glossary</div>
                  <div className="text-sm text-muted-foreground">{error}</div>
                </div>
              ) : safeTerms.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-muted-foreground mb-2">No terms found</div>
                  <div className="text-sm text-muted-foreground/80">
                    {searchQuery || (selectedTags && selectedTags.length > 0)
                      ? "Try adjusting your search or filters"
                      : "No glossary terms have been added yet"
                    }
                  </div>
                </div>
              ) : (
                <div>
                  <div className="mb-4 text-sm text-muted-foreground">
                    Showing {safeTerms.length} term{safeTerms.length !== 1 ? 's' : ''}
                    {searchQuery && ` matching "${searchQuery}"`}
                    {selectedTags && selectedTags.length > 0 && ` with tags: ${selectedTags.join(', ')}`}
                  </div>
                  
                  {safeTerms.map(renderTermCard)}

                  {loading && (
                    <div className="py-4 text-center">
                      <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                      <span className="ml-2 text-sm text-muted-foreground">Loading more terms...</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GlossaryModal;